import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Accordion from "src/components/Accordion";
import Audit from "src/components/Audit";
import LovCommercialPlansOfBusinessGroup from "src/components/Lov/CommercialPlansOfBusinessGroup";
import Modal from "src/components/Modal";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { IEftba002 } from "src/pages/Profile/interface";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { IBusinessGroupContractData, IRefModalCommercialPlan, IRefModalViewContract } from "./interface";
import ListContractItems from "./listContractItems";
import ModalChangeCommercialPlan from "./modalChangeCommercialPlan";

const ModalViewContract: React.ForwardRefRenderFunction<IRefModalViewContract> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const {addToast} = RCCHookToast();
	const [selectedGroupCompanyContractData, setSelectedGroupCompanyContractData] = useState<IBusinessGroupContractData>({} as IBusinessGroupContractData);

	const onCallbackFunction = useRef<Function>();
    
	// Modal View Contract
	const refModalChangeCommercialPlan = useRef<IRefModalCommercialPlan>(null);

	const modalViewContract = useRef<RCCRefModalProps>(null);
	const [subtitle, setSubtitle] = useState("");
	const [efa001Id, setEfa001Id] = useState(0);
	const { control } = useForm();
	
	const actionButtonViewContract = async (id: number, name: string, callbackChange?: Function) => {
		try{
			setEfa001Id(id);
			const {data} = await RCCServicesApi.getRequest({
				method: "BusinessGroup/ViewContract",
				urlParameters: {
					id
				}
			});
			onCallbackFunction.current = callbackChange;
			setSubtitle(name);
			setSelectedGroupCompanyContractData(data);
			modalViewContract.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao selecionar dados do contrato"), addToast);
		}
	};

	const callback = (contract: IBusinessGroupContractData) => {
		modalViewContract.current?.closeModal();
		setSelectedGroupCompanyContractData(contract);
		window.setTimeout(() => {
			modalViewContract.current?.openModal();
		}, 200);
	};

	useImperativeHandle(ref, () => ({
		actionButtonViewContract
	}));
    
	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalViewContract}
				header={{
					title: translate("Itens do contrato"),
					subtitle: `${translate("Grupo empresa")}: ${subtitle}`,
					icon: {
						name: "list"
					}
				}}
				onClose={() => {
					if(onCallbackFunction.current)
						onCallbackFunction.current();
				}}
				footer={{
					showSaveButton: false,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				withoutForm
			>
				<Tabs 
					name="tabsModalViewContract"
					tabInit={0}
					options={[{
						label: translate("Grupo empresa"),
						content: <>
							<RCCGrid container item alignItems="center" justifyContent="space-between">
								<RCCGrid container item xs={12} sm={12} md={6} lg={4} alignItems="center">
									{efa001Id > 0 && (
										<RCCGrid container item style={{ flex: 1}}>
											<LovCommercialPlansOfBusinessGroup 
												efa001Id={efa001Id}
												label={translate("Plano comercial")}
												name="efa001Id"
												control={control}
												defaultValue={selectedGroupCompanyContractData && selectedGroupCompanyContractData.efe001Id > 0 ? {
													value: selectedGroupCompanyContractData.efe001Id.toString(),
													label: selectedGroupCompanyContractData.efe001Efd001Descricao.toString()
												} : undefined}
												onChangeCapture={(e: any) => {
													if(Number(e.value) === selectedGroupCompanyContractData.efe001Id)
														return;
												
													callback({
														...e.extendedProps,
														efe001Id: Number(e.value),
														efe001Efd001Descricao: e.label
													});
												}}
											/>
										</RCCGrid>
									)} &nbsp;

									<RCCTooltip
										text={translate("Trocar plano comercial")}
										color="purple"
									>
										<RCCButton 
											color="purple"
											icon={{
												name: "refresh"
											}}
											layout="isRounded"
											size="md"
											onClick={() => refModalChangeCommercialPlan.current?.actionButtonChange(callback)}
										/>
									</RCCTooltip>

									{selectedGroupCompanyContractData.efe001Id > 0 && (
										<RCCTooltip
											text={translate("Excluir plano comercial")}
											color="red"
										>
											<RCCButton 
												color="red"
												icon={{
													name: "trash"
												}}
												layout="isRounded"
												size="md"
												onClick={() => 
													refModalChangeCommercialPlan.current?.actionButtonDelete(
														selectedGroupCompanyContractData.efe001Id, 
														selectedGroupCompanyContractData.efe001Efd001Descricao,
														() => {
															modalViewContract.current?.closeModal();
															actionButtonViewContract(selectedGroupCompanyContractData.efe001Efa001Id, subtitle);
														}
													)
												}
											/>
										</RCCTooltip>
									)}
								</RCCGrid>
								<Title color="defaultColor" size="sm" styleInline="margin: 0">
									<strong>
										{translate("Período de validade")} 
									</strong>
										: {formatDate(selectedGroupCompanyContractData.efe001Dtinicio, currentLanguage)} - {formatDate(selectedGroupCompanyContractData.efe001Dtfim, currentLanguage)} &nbsp;

									{selectedGroupCompanyContractData.efe001Id > 0 && (
										<RCCTooltip
											text={translate("Revalidar plano comercial")}
											color="purple"
										>
											<RCCButton 
												color="purple"
												icon={{
													name: "calendar"
												}}
												layout="isRounded"
												size="md"
												onClick={() => refModalChangeCommercialPlan.current?.actionButtonRevalidate(selectedGroupCompanyContractData.efe001Efd001Id, selectedGroupCompanyContractData.efe001Dtinicio, callback)}
											/>
										</RCCTooltip>
									)}
									<Audit 
										data={selectedGroupCompanyContractData} 
										type="pop" 
										position="left"
									/> 
								</Title>
							</RCCGrid>
							{selectedGroupCompanyContractData  
							&& selectedGroupCompanyContractData.efe001Id > 0 
							&& (
								<ListContractItems 
									efa001Id={selectedGroupCompanyContractData.efe001Efa001Id}
									dateStart={selectedGroupCompanyContractData.efe001Dtinicio}
									dateEnd={selectedGroupCompanyContractData.efe001Dtfim}
								/>
							)}
						</>
					},{
						label: translate("Empresas"),
						content: <RCCGrid container item direction="column">
							{selectedGroupCompanyContractData  
							&& selectedGroupCompanyContractData.efe001Id > 0
							&& selectedGroupCompanyContractData.eftba002
							&& selectedGroupCompanyContractData.eftba002.length > 0 
							&& selectedGroupCompanyContractData.eftba002.map((efa002: IEftba002) => <Accordion
								header={
									<RCCGrid
										container
										item
									>
										<Title 
											color="purple"		
											size="sm"								
										>
											{efa002.efa002Fantasia}
										</Title>
									</RCCGrid>
								}
								key={efa002.efa002Id}
							>
								<ListContractItems 
									efa001Id={selectedGroupCompanyContractData.efe001Efa001Id}
									efa002Id={efa002.efa002Id}
									dateStart={selectedGroupCompanyContractData.efe001Dtinicio}
									dateEnd={selectedGroupCompanyContractData.efe001Dtfim}
								/>
							</Accordion>)}
						</RCCGrid>
					}]}
				/>
			</Modal>

			<ModalChangeCommercialPlan 
				ref={refModalChangeCommercialPlan}
				efa001Id={efa001Id}
			/>
		</>
	);
};

export default forwardRef(ModalViewContract);
